// Marketplace identity + creator directory.
//
// Follows the house convention (inline gql + hand-written interface + thin
// wrapper hook) rather than the codegen pipeline, which emits no hooks and has
// zero importers. useMonitor.ts is the model.

import { gql, useQuery, useMutation } from "@apollo/client";
import { useActiveBrand } from "@/hooks/useActiveBrand";

export type MarketplaceRole = "BRAND" | "CREATOR";
export type SocialPlatform = "INSTAGRAM" | "TIKTOK" | "YOUTUBE" | "X";

export interface SocialAccount {
  id: string;
  platform: SocialPlatform;
  handle: string;
  url: string | null;
  followers: number | null;
  engagementRate: number | null;
  avgViews: number | null;
  /**
   * Where the numbers came from. Prefer this over `verified`, which only
   * separates OAuth from everything else and so labels official platform data
   * and a creator's own guess identically.
   */
  dataSource: "PUBLIC_API" | "SELF_REPORTED" | "OAUTH_VERIFIED" | "FIXTURE";
  lastSyncedAt: string | null;
  verified: boolean;
  verifiedAt: string | null;
}

export interface CreatorProfile {
  id: string;
  handle: string;
  displayName: string;
  bio: string | null;
  avatarUrl: string | null;
  /** Null when it could not be determined from public data. */
  country: string | null;
  city: string | null;
  languages: string[];
  niches: string[];
  contentFormats: string[];
  minRate: number | null;
  baseCurrency: string;
  acceptsGifting: boolean;
  portfolioUrls: string[];
  isPublished: boolean;
  source: "SELF_REGISTERED" | "DISCOVERED" | "IMPORTED";
  claimedAt: string | null;
  /** Fields we inferred. Never render these as the creator's own words. */
  derivedFields: string[];
  avgRating: number | null;
  completionRate: number | null;
  totalCollaborations: number;
  socialAccounts: SocialAccount[];
}

export interface BrandProfile {
  id: string;
  companyName: string;
  websiteUrl: string | null;
  logoUrl: string | null;
  industry: string | null;
  description: string | null;
  country: string;
  defaultCurrency: string;
  /** Platform take for this account. Set by an operator, read-only here. */
  commissionPct: number;
}

export interface CreatorSearchInput {
  query?: string;
  countries?: string[];
  niches?: string[];
  platforms?: SocialPlatform[];
  minFollowers?: number;
  maxRate?: number;
  acceptsGifting?: boolean;
  verifiedOnly?: boolean;
}

const SOCIAL_ACCOUNT_FIELDS = gql`
  fragment SocialAccountFields on SocialAccount {
    id
    platform
    handle
    url
    followers
    engagementRate
    avgViews
    dataSource
    lastSyncedAt
    verified
    verifiedAt
  }
`;

const CREATOR_PROFILE_FIELDS = gql`
  ${SOCIAL_ACCOUNT_FIELDS}
  fragment CreatorProfileFields on CreatorProfile {
    id
    handle
    displayName
    bio
    avatarUrl
    country
    city
    languages
    niches
    contentFormats
    minRate
    baseCurrency
    acceptsGifting
    portfolioUrls
    isPublished
    source
    claimedAt
    derivedFields
    avgRating
    completionRate
    totalCollaborations
    socialAccounts {
      ...SocialAccountFields
    }
  }
`;

const BRAND_PROFILE_FIELDS = gql`
  fragment BrandProfileFields on BrandProfile {
    id
    companyName
    websiteUrl
    logoUrl
    industry
    description
    country
    defaultCurrency
    commissionPct
  }
`;

const SEARCH_CREATORS = gql`
  ${CREATOR_PROFILE_FIELDS}
  query SearchCreators($input: CreatorSearchInput, $first: Int, $after: String) {
    searchCreators(input: $input, first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          ...CreatorProfileFields
        }
      }
    }
  }
`;

const CREATOR_BY_HANDLE = gql`
  ${CREATOR_PROFILE_FIELDS}
  query CreatorByHandle($handle: String!) {
    creatorByHandle(handle: $handle) {
      ...CreatorProfileFields
    }
  }
`;

const MY_CREATOR_PROFILE = gql`
  ${CREATOR_PROFILE_FIELDS}
  query MyCreatorProfile {
    myCreatorProfile {
      ...CreatorProfileFields
    }
  }
`;

const MY_BRAND_PROFILE = gql`
  ${BRAND_PROFILE_FIELDS}
  query MyBrandProfile {
    myBrandProfile {
      ...BrandProfileFields
    }
  }
`;

const MY_BRAND_PROFILES = gql`
  ${BRAND_PROFILE_FIELDS}
  query MyBrandProfiles {
    myBrandProfiles {
      ...BrandProfileFields
    }
  }
`;

const PARSE_CREATOR_SEARCH = gql`
  mutation ParseCreatorSearch($query: String!) {
    parseCreatorSearch(query: $query) {
      interpretation
      unmapped
      filters {
        query
        countries
        niches
        platforms
        minFollowers
        maxRate
        acceptsGifting
        verifiedOnly
      }
    }
  }
`;

const SET_MARKETPLACE_ROLE = gql`
  mutation SetMarketplaceRole($role: MarketplaceRole!) {
    setMarketplaceRole(role: $role) {
      id
      marketplaceRole
      onboardingCompleted
    }
  }
`;

const UPSERT_CREATOR_PROFILE = gql`
  ${CREATOR_PROFILE_FIELDS}
  mutation UpsertCreatorProfile($input: CreatorProfileInput!) {
    upsertCreatorProfile(input: $input) {
      ...CreatorProfileFields
    }
  }
`;

const UPSERT_BRAND_PROFILE = gql`
  ${BRAND_PROFILE_FIELDS}
  mutation UpsertBrandProfile($input: BrandProfileInput!, $brandProfileId: ID) {
    upsertBrandProfile(input: $input, brandProfileId: $brandProfileId) {
      ...BrandProfileFields
    }
  }
`;

const CREATE_BRAND_PROFILE = gql`
  ${BRAND_PROFILE_FIELDS}
  mutation CreateBrandProfile($input: BrandProfileInput!) {
    createBrandProfile(input: $input) {
      ...BrandProfileFields
    }
  }
`;

const UPSERT_SOCIAL_ACCOUNT = gql`
  ${SOCIAL_ACCOUNT_FIELDS}
  mutation UpsertSocialAccount($input: SocialAccountInput!) {
    upsertSocialAccount(input: $input) {
      ...SocialAccountFields
    }
  }
`;

const DELETE_SOCIAL_ACCOUNT = gql`
  mutation DeleteSocialAccount($id: ID!) {
    deleteSocialAccount(id: $id)
  }
`;

interface CreatorConnection {
  totalCount: number;
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
  edges: Array<{ cursor: string; node: CreatorProfile }>;
}

export function useCreatorSearch(input: CreatorSearchInput, first = 24) {
  const { data, loading, error, fetchMore } = useQuery<{
    searchCreators: CreatorConnection;
  }>(SEARCH_CREATORS, {
    variables: { input, first },
    fetchPolicy: "cache-and-network",
  });

  const connection = data?.searchCreators;

  return {
    creators: connection?.edges.map((e) => e.node) ?? [],
    totalCount: connection?.totalCount ?? 0,
    hasNextPage: connection?.pageInfo.hasNextPage ?? false,
    loading,
    error,
    /** Appends the next page; Apollo merges via the searchCreators type policy. */
    loadMore: () =>
      fetchMore({
        variables: { input, first, after: connection?.pageInfo.endCursor },
      }),
  };
}

export function useCreatorByHandle(handle: string | undefined) {
  const { data, loading, error } = useQuery<{ creatorByHandle: CreatorProfile | null }>(
    CREATOR_BY_HANDLE,
    { variables: { handle }, skip: !handle },
  );

  return { creator: data?.creatorByHandle ?? null, loading, error };
}

export interface ParsedCreatorSearch {
  filters: CreatorSearchInput;
  interpretation: string;
  /** Criteria the schema cannot express. Show these — never drop them. */
  unmapped: string[];
}

/**
 * Turn a plain-language description into search filters.
 *
 * A mutation, so it never runs from a cache or a prefetch — each call costs
 * money. The result is a *proposal*: the caller writes it into the existing
 * filter state, where the user can see and correct it.
 */
export function useParseCreatorSearch() {
  const [mutate, { loading, error }] = useMutation<{
    parseCreatorSearch: ParsedCreatorSearch;
  }>(PARSE_CREATOR_SEARCH);

  return {
    parse: async (query: string) => {
      const { data } = await mutate({ variables: { query } });
      if (!data?.parseCreatorSearch) throw new Error("No se pudo interpretar la búsqueda.");
      return data.parseCreatorSearch;
    },
    loading,
    error,
  };
}

export function useMyCreatorProfile() {
  const { data, loading, error, refetch } = useQuery<{
    myCreatorProfile: CreatorProfile | null;
  }>(MY_CREATOR_PROFILE, { fetchPolicy: "cache-and-network" });

  return { profile: data?.myCreatorProfile ?? null, loading, error, refetch };
}

export function useMyBrandProfile() {
  const { data, loading, error, refetch } = useQuery<{ myBrandProfile: BrandProfile | null }>(
    MY_BRAND_PROFILE,
    { fetchPolicy: "cache-and-network" },
  );

  return { profile: data?.myBrandProfile ?? null, loading, error, refetch };
}

/**
 * Every brand the caller manages — an agency has one per client.
 *
 * Skipped for creators: the query is brand-only and would just return an empty
 * list after a round trip.
 */
export function useMyBrandProfiles(enabled = true) {
  const { data, loading, error, refetch } = useQuery<{ myBrandProfiles: BrandProfile[] }>(
    MY_BRAND_PROFILES,
    { fetchPolicy: "cache-and-network", skip: !enabled },
  );

  return { brands: data?.myBrandProfiles ?? [], loading, error, refetch };
}

/**
 * The brand the caller is currently acting as, resolved the same way the server
 * resolves it: the named one, or the oldest when none is named. Both
 * `requireBrandProfile` and `listForOwner` order by `createdAt: 'asc'`, so
 * `brands[0]` is the same row the server would pick — a screen that shows terms
 * of the account must not disagree with the account the mutation writes to.
 */
export function useActiveBrandProfile(enabled = true) {
  const { brands, loading, error } = useMyBrandProfiles(enabled);
  const { activeBrandId } = useActiveBrand();

  const brand = (activeBrandId ? brands.find((b) => b.id === activeBrandId) : null) ?? brands[0] ?? null;

  // Distinguished from `!brand` so a screen can tell "still loading" from
  // "this account has no brand yet" — the second needs a call to action, and
  // showing it while the query is in flight is a flash of the wrong screen.
  return { brand, loading, error, hasNoBrand: !loading && brands.length === 0 };
}

const DISCOVERY_PROVIDER_NAME = gql`
  query DiscoveryProviderName {
    discoveryProviderName
  }
`;

/**
 * Which provider is behind the catalogue. Operator-only server-side, so this is
 * skipped entirely for everyone else rather than fired and left to fail — a
 * FORBIDDEN on every directory render would be noise in the console and a
 * wasted round trip.
 */
export function useDiscoveryProviderName(enabled: boolean) {
  const { data } = useQuery<{ discoveryProviderName: string }>(DISCOVERY_PROVIDER_NAME, {
    skip: !enabled,
  });

  return { providerName: data?.discoveryProviderName ?? null };
}

export interface SetRoleResult {
  id: string;
  marketplaceRole: MarketplaceRole;
  onboardingCompleted: boolean;
}

/**
 * Sets the role and returns the updated user.
 *
 * Deliberately does NOT rely on `refetchQueries: ["GetMe"]` to propagate the
 * change. AuthContext loads its user once through an imperative
 * `client.query(GET_ME)` and holds it in React state — it never subscribes — so
 * refetching GetMe updates the Apollo cache while `useAuth().user` stays stale.
 * The caller must feed this result into `updateUser`, or the route guard reads
 * a null role and bounces straight back to the picker.
 */
export function useSetMarketplaceRole() {
  const [mutate, { loading, error }] = useMutation<{ setMarketplaceRole: SetRoleResult }>(
    SET_MARKETPLACE_ROLE,
  );

  return {
    setRole: async (role: MarketplaceRole) => {
      const { data } = await mutate({ variables: { role } });
      if (!data?.setMarketplaceRole) throw new Error("No se pudo guardar tu elección.");
      return data.setMarketplaceRole;
    },
    loading,
    error,
  };
}

export function useUpsertCreatorProfile() {
  const [mutate, { loading, error }] = useMutation(UPSERT_CREATOR_PROFILE, {
    refetchQueries: ["MyCreatorProfile"],
  });

  return {
    save: async (input: Partial<CreatorProfile>) => {
      const { data } = await mutate({ variables: { input } });
      return data?.upsertCreatorProfile as CreatorProfile;
    },
    loading,
    error,
  };
}

export function useUpsertBrandProfile() {
  // MyBrandProfiles too: the switcher and every brand-scoped screen read the
  // list, and an edit that only refreshed the singular query left the switcher
  // showing the old company name.
  const [mutate, { loading, error }] = useMutation(UPSERT_BRAND_PROFILE, {
    refetchQueries: ["MyBrandProfile", "MyBrandProfiles"],
  });

  return {
    save: async (input: Partial<BrandProfile>, brandProfileId?: string) => {
      const { data } = await mutate({ variables: { input, brandProfileId } });
      return data?.upsertBrandProfile as BrandProfile;
    },
    loading,
    error,
  };
}

/** Add another brand. See `createBrandProfile` — upsert can only ever edit. */
export function useCreateBrandProfile() {
  const [mutate, { loading, error }] = useMutation(CREATE_BRAND_PROFILE, {
    refetchQueries: ["MyBrandProfile", "MyBrandProfiles"],
  });

  return {
    create: async (input: Partial<BrandProfile>) => {
      const { data } = await mutate({ variables: { input } });
      return data?.createBrandProfile as BrandProfile;
    },
    loading,
    error,
  };
}

export function useSocialAccounts() {
  const [upsert, { loading: saving }] = useMutation(UPSERT_SOCIAL_ACCOUNT, {
    refetchQueries: ["MyCreatorProfile"],
  });
  const [remove, { loading: removing }] = useMutation(DELETE_SOCIAL_ACCOUNT, {
    refetchQueries: ["MyCreatorProfile"],
  });

  return {
    saveAccount: async (input: {
      platform: SocialPlatform;
      handle: string;
      url?: string;
      followers?: number;
      engagementRate?: number;
    }) => {
      const { data } = await upsert({ variables: { input } });
      return data?.upsertSocialAccount as SocialAccount;
    },
    deleteAccount: async (id: string) => {
      const { data } = await remove({ variables: { id } });
      return Boolean(data?.deleteSocialAccount);
    },
    saving,
    removing,
  };
}
